import { DROP_LOCATIONS, FAIL_REASONS, ORDER_STATUSES, SIGNED_BY_TYPES } from './status';

/**
 * 面向人的中文文案。
 *
 * ★ 放在 shared 而不是各端自己写一份 —— 司机端选「客户拒收」,
 *   管理端时间轴必须也显示「客户拒收」。两边各写一遍,迟早一边改了另一边没改,
 *   客服和司机对着同一条记录说两种话。
 *
 * 只放**跨端共享**的文案。各端自己的界面用语(按钮、提示)留在各端。
 */

export const FAIL_REASON_LABEL: Record<(typeof FAIL_REASONS)[number], string> = {
  NO_ONE_HOME: '无人接收',
  PHONE_UNREACHABLE: '电话打不通',
  WRONG_ADDRESS: '地址错误 / 找不到',
  REFUSED: '客户拒收',
  NO_ACCESS: '无法进入',
  DAMAGED: '货物破损',
  RESCHEDULED: '客户要求改约',
  OTHER: '其他',
};

export const DROP_LOCATION_LABEL: Record<(typeof DROP_LOCATIONS)[number], string> = {
  DOOR: '门口',
  RECEPTION: '前台',
  SECURITY: '保安',
  LOCKER: '快递柜',
  OTHER: '其他',
};

export const GOODS_DISPOSITION_LABEL: Record<string, string> = {
  RETURNED_TO_SENDER: '退回发货方',
  HELD_BY_DRIVER: '司机暂存',
  DISPOSED: '已处置',
};

export const SIGNED_BY_LABEL: Record<(typeof SIGNED_BY_TYPES)[number], string> = {
  SELF: '本人签收',
  PROXY: '他人代收',
  LEFT_AT_LOCATION: '放置指定位置',
};

/** 状态在管理端的说法。司机端有自己的一套(「待领取」vs「待派单」),不共用 */
export const ADMIN_STATUS_LABEL: Record<(typeof ORDER_STATUSES)[number], string> = {
  DRAFT: '草稿',
  PENDING: '待派单',
  ASSIGNED: '待接单',
  ACCEPTED: '待取货',
  PICKED_UP: '配送中',
  IN_TRANSIT: '配送中',
  DELIVERED: '已送达',
  FAILED: '未送达',
  RETURNED: '已退回',
  CANCELLED: '已取消',
};

/**
 * 状态的中文,**看开关说话**。
 *
 * ★ `PICKED_UP` 和 `IN_TRANSIT` 在上面那张表里都叫「配送中」——
 *   关着「配送中」这个状态时那是对的:取到货就等于在路上了。
 *
 *   但开着的时候,屏幕上会同时出现两个「配送中」:
 *   地图图例里两行同名不同色,订单列表里两种单看不出差别 ——
 *   等于把这个开关买来的那点信息又扔掉了。
 *   开着时 `PICKED_UP` 应该叫「已取货」。
 */
export function statusLabel(
  status: (typeof ORDER_STATUSES)[number],
  inTransitOn: boolean,
): string {
  if (status === 'PICKED_UP' && inTransitOn) return '已取货';
  return ADMIN_STATUS_LABEL[status];
}

/**
 * 事件类型的中文。
 * 覆盖状态机的 15 个动作,外加几个非状态变更的事件(地理编码、区域匹配、备注)。
 */
export const EVENT_LABEL: Record<string, string> = {
  // 状态机的 15 个动作
  CONFIRM: '确认订单',
  ASSIGN: '派单',
  DISPATCH_TO_CHANNEL: '交给三方承运方',
  GRAB: '司机抢单',
  ACCEPT: '司机接单',
  REJECT: '司机拒单',
  EXPIRE: '接单超时',
  UNASSIGN: '撤回派单',
  TRANSFER: '转派',
  PICKUP: '取货',
  START_TRANSIT: '开始配送',
  DELIVER: '送达',
  FAIL: '投递失败',
  RETRY: '再次投递',
  RETURN: '退回',
  CANCEL: '取消',
  // 非状态变更的事件
  CREATED: '建单',
  ZONE_MATCHED: '匹配区域',
  GEOCODED: '地址解析',
  NOTE_ADDED: '添加备注',
  /**
   * 备注被**改写**(不是往后追加)。
   *
   * ★ 和 NOTE_ADDED 分开记,时间轴上才看得出区别:
   *   追加是「又交代了一句」,改写是「原来那句不作数了」——
   *   出纠纷时要回答的正是「司机当时看到的是哪一句」,
   *   而两种都记成「添加备注」的话,这个问题答不了。
   */
  NOTE_EDITED: '修改备注',
  /**
   * 司机端那一条操作最终没发出去,队列放弃了。
   *
   * ★ 司机端不再为这件事弹任何东西(他在路上,而且多半已经用别的方式
   *   把活干完了)。但它必须落在**这一单的时间轴上**:
   *   一条「送达」就这么没了而没人知道,是这套系统里最坏的一种静默失败。
   *   调度看得见,才有人能去问一句。
   */
  SYNC_ABANDONED: '司机端操作未能同步(已放弃)',
  /**
   * 期望送达日期被改了。
   *
   * ★ 为什么要记:这一列决定**司机端今天列不列这一单**、
   *   以及过了那天要不要提醒他。一单莫名其妙从司机的当日列表里消失,
   *   而时间轴上没有任何痕迹 —— 那是最难查的一类:
   *   没人记得是谁在什么时候把它推到了明天。
   */
  EXPECTED_DELIVERY_CHANGED: '改了期望送达日期',
  ADDRESS_REVIEWED: '地址复核',
  PHONE_CHANGED: '改了收件人电话',
  /** 商家系统通过开放接口改了收货信息 / 备注 */
  RECEIVER_UPDATED: '商家系统改了收货信息',
  PUSHED_TO_CHANNEL: '已推给三方平台',
  NOTIFIED_CHANNEL: '已通知商家系统',
  CHANNEL_CALLBACK: '三方回调(未改状态)',
  REPRICED: '重算运费',
  HANDLING_UPDATED: '改了代收 / 特殊物品',
  PAYOUT_UPDATED: '改了司机酬劳',
};

export const ACTOR_LABEL: Record<string, string> = {
  USER: '管理员',
  DRIVER: '司机',
  SYSTEM: '系统',
  THIRD_PARTY: '三方平台',
};

/**
 * 凭证类型。
 *
 * ★ 和上传 purpose 用同一套词表 —— 前端按 purpose 上传、后端按 type 存,
 *   两边叫法不一致的话管理端只能显示原始码值(曾经就是 `PHOTO`)
 */
export const PROOF_LABEL: Record<string, string> = {
  PICKUP_PHOTO: '取货照片',
  POD_PHOTO: '签收照片',
  FAIL_PHOTO: '异常现场',
  RETURN_PHOTO: '退回凭证',
  SIGNATURE: '签名',
};

/**
 * 安全查表。
 *
 * 上面几个 Record 用的是精确 key 类型 —— 加了新枚举而没加文案时,
 * TypeScript 会在**定义处**报错,这正是想要的。
 * 但从接口读回来的是 `string`,直接索引过不了类型检查,
 * 而且历史数据里可能有已经废弃的码值。用这个函数查:查不到就显示原文,
 * 不显示 undefined,也不崩。
 */
export function labelOf(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '';
  return map[code] ?? code;
}
